"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { hasConsent } from "@/lib/consent";

// Everything the live WordPress site (luluwild.co.uk) loads, audited 2026-10-14.
// Full table in projects/luluwild/research/TRACKING.md.
//
// - Google Tag Manager GTM-MVJJJ2K (same container as live). Inside it: GA4 G-E2S9C6J9PW,
//   Google tag GT-KTRGHVZP, Google Ads AW-10969824126 with two conversions (one on any
//   /reservations page load, one on OpenTable's ot-reservation-made), the GA4 event
//   "Booking Confirmed - Thank You Page", a Meta "Purchase" tag for OpenTable bookings and the
//   conversion linker (cross-domain with luluwild.giftpro.co.uk).
// - Meta pixel 173699988185787 base code, plus Conversions API via functions/api/meta-event.js. On live this came from the PixelYourSite Pro plugin,
//   not GTM, so it is carried over here. Do not also add a base tag in GTM (double PageViews).
//   PixelYourSite's events are mirrored: PageView, TelClick, EmailClick, Form, and Lead on the
//   venue hire / Christmas party enquiry form (Gravity Form 4 on live).
// - Klaviyo onsite JS (klaviyo.js) is NOT loaded (removed 2026-10-22, Anthony): it injected the client's
//   "Join Lulu" popup, an extra H1 and a scroll lock. Sign-ups go straight to Klaviyo through
//   /api/klaviyo-subscribe (lib/klaviyo.js, functions/api/klaviyo-subscribe.js), which does not need it.
//
// Consent: matches the live site. GTM loads immediately (Complianz ran it as "functional"); the
// container's consent template sets the defaults. The Meta pixel waits for Accept.
const GTM_ID = "GTM-MVJJJ2K";
const GA4_ID = "G-E2S9C6J9PW";
const META_PIXEL_ID = "173699988185787";

// W3Form leaves the enquiry details here on submit; the confirmation page fires the conversion
// once (the Gravity Forms equivalent of "form submitted and confirmed").
export const LEAD_KEY = "lw_lead_conversion";

// Tags only run on the real domain, so localhost and lulu.eneje.co.uk never send test hits into the
// client's GA4, Ads, Meta or Klaviyo. To test tracking elsewhere, open any page with ?tracking=on
// (stays on for that tab; ?tracking=off turns it off again).
const LIVE_HOSTS = ["luluwild.co.uk", "www.luluwild.co.uk"];
function trackingEnabled() {
  try {
    const q = new URLSearchParams(window.location.search).get("tracking");
    if (q === "on") sessionStorage.setItem("lw_tracking", "on");
    if (q === "off") sessionStorage.removeItem("lw_tracking");
    return LIVE_HOSTS.includes(window.location.hostname) || sessionStorage.getItem("lw_tracking") === "on";
  } catch {
    return LIVE_HOSTS.includes(window.location.hostname);
  }
}

export function track(event, data = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...data });
}

function addScript(id, src) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id; s.async = true; s.src = src;
  document.head.appendChild(s);
}

function loadGtm() {
  if (document.getElementById("gtm-script")) return;
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  addScript("gtm-script", `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
}

function loadMeta() {
  if (window.fbq) return;
  const n = (window.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); });
  if (!window._fbq) window._fbq = n;
  n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
  addScript("meta-pixel", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", META_PIXEL_ID);
}

// Meta Conversions API: server-side twin of each pixel event (functions/api/meta-event.js). Both share
// one event ID so Meta counts the event once; the server copy gets past ad blockers and Safari
// tracking prevention (what PixelYourSite's CAPI did on the WordPress site). The function only sends
// for luluwild.co.uk, and the token stays server-side. Contact details (forms only) are hashed there.
export const newEventId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
export function metaServer(name, eventId, custom = {}, user) {
  try {
    fetch("/api/meta-event", {
      method: "POST", keepalive: true, headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event_name: name, event_id: eventId, url: window.location.href, custom, user }),
    }).catch(() => {});
  } catch {}
}
// Pixel + server copy in one call. Custom events (TelClick etc.) use trackCustom, as PixelYourSite did.
export function metaEvent(name, params = {}, { custom = false, user, eventId } = {}) {
  if (!window.fbq) return;
  const id = eventId || newEventId();
  window.fbq(custom ? "trackCustom" : "track", name, params, { eventID: id });
  metaServer(name, id, params, user);
}

function fireLeadConversion() {
  let lead;
  try { lead = JSON.parse(sessionStorage.getItem(LEAD_KEY) || "null"); sessionStorage.removeItem(LEAD_KEY); } catch { return; }
  if (!lead) return;
  const params = {
    form_id: lead.form_id,
    form_name: lead.form_name,
    event_type: lead.event_type,
    guests: lead.guests,
    event_date: lead.event_date,
    start_time: lead.start_time,
    catering_style: lead.catering_style,
    entertainment: lead.entertainment,
    budget: lead.budget,
    form_page: lead.page_path,
  };
  // GA4: standard lead event with the event details as parameters. Register event_type, guests,
  // catering_style and budget as custom dimensions in GA4 to report on them.
  window.gtag("event", "generate_lead", { send_to: GA4_ID, ...params });
  // dataLayer copy for GTM, named after the old Gravity Forms form. Only add a GTM tag on this if
  // generate_lead is not already being used as the conversion (double counting).
  track("gform_submission", params);
  // Meta: same event PixelYourSite sent for Gravity Form 4. If W3Form already sent the server copy
  // (with hashed contact details) at submit, reuse its event ID so Meta de-duplicates.
  const leadParams = { content_name: lead.form_name, content_category: lead.event_type || "Event enquiry", num_guests: lead.guests };
  if (!window.fbq) return;
  const id = lead.meta_event_id || newEventId();
  window.fbq("track", "Lead", leadParams, { eventID: id });
  if (!lead.meta_server_sent) metaServer("Lead", id, leadParams);
}

export default function Tracking() {
  const pathname = usePathname();

  useEffect(() => {
    if (!trackingEnabled()) return;
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = window.gtag || gtag;
    // Same as the live site: GTM loads on every page straight away, and the container's own consent
    // template sets the Consent Mode defaults (analytics + ads granted). No default is set here, so the
    // container's defaults apply. A visitor who clicks Decline is switched to denied.
    loadGtm();

    // Meta pixel waits for Accept, as Complianz did on the live site.
    const loadMarketing = () => {
      const first = !window.fbq;
      loadMeta();
      if (first) metaEvent("PageView");
    };
    if (hasConsent()) loadMarketing();
    fireLeadConversion();

    const onConsent = (e) => {
      if (e.detail?.granted) {
        gtag("consent", "update", { ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted", analytics_storage: "granted" });
        loadMarketing();
      } else {
        gtag("consent", "update", { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied" });
      }
    };
    window.addEventListener("lw:consent", onConsent);

    const onClick = (e) => {
      const a = e.target.closest("a"); if (!a) return;
      const href = a.getAttribute("href") || "";
      // PixelYourSite automatic events.
      if (href.startsWith("tel:")) metaEvent("TelClick", { link: href }, { custom: true });
      if (href.startsWith("mailto:")) metaEvent("EmailClick", { link: href }, { custom: true });
      // The container's Ads conversion fires on a full page load of /reservations, and gtm.js
      // only runs on full loads. Force a real navigation so client-side routing never skips it.
      if (/^\/reservations\/?(#|\?|$)/.test(href) && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
        e.preventDefault();
        window.location.assign(href);
      }
    };
    // Capture phase so this runs before next/link, which then sees defaultPrevented and stands down.
    document.addEventListener("click", onClick, true);

    const onSubmit = (e) => {
      if (e.target?.tagName === "FORM") metaEvent("Form", { form_page: window.location.pathname }, { custom: true });
    };
    document.addEventListener("submit", onSubmit, true);

    return () => {
      window.removeEventListener("lw:consent", onConsent);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, true);
    };
  }, []);

  // Meta PageView on client-side navigation (GA4 picks up history changes itself via
  // enhanced measurement; the first load is counted when consent loads the pixel).
  useEffect(() => {
    if (window.fbq && window.__lwLastPath && window.__lwLastPath !== pathname) metaEvent("PageView");
    window.__lwLastPath = pathname;
  }, [pathname]);

  return null;
}
